<template>
  <div
    class="RenderAuthAvatar relative"
    :class="{ 'overflow-x-hidden': !compact }"
  >
    <ClientOnly>
      <UUser
        v-if="!compact"
        :name="userName"
        :description="userEmail"
        :avatar="avatarProps"
        size="xl"
      />

      <UAvatar
        v-else
        :src="avatarSrc"
        :alt="userAlt"
        size="md"
        :chip="avatarChipConfig"
        :as="avatarAsConfig"
      />
    </ClientOnly>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { AvatarProps } from '@nuxt/ui'
import { useLoginComposable } from '~~/modules/auth/composables/useLoginComposable'

const { RenderAuthUserInstance } = useLoginComposable()

const { compact, sidebarLayout } = defineProps<{
  compact?: boolean
  sidebarLayout?: boolean
}>()

const showUnreadBadge = computed<boolean>(() => {
  return RenderAuthUserInstance?.value?.show_unread ?? false
})

const avatarChipConfig = computed<false | NonNullable<AvatarProps['chip']>>(
  () => {
    if (!showUnreadBadge.value) return false

    return {
      text: sidebarLayout ? '' : '!',
      color: 'error',
      position: 'top-right',
      size: '3xl',
    }
  },
)

const avatarAsConfig = computed<NonNullable<AvatarProps['as']>>(() => ({
  root: 'span',
  img: 'img',
}))

const userName = computed<string>(() => {
  return RenderAuthUserInstance?.value?.name ?? ''
})

const userEmail = computed<string>(() => {
  return RenderAuthUserInstance?.value?.mail ?? ''
})

const avatarSrc = computed<string>(() => {
  return RenderAuthUserInstance?.value?.avatar ?? ''
})

const userAlt = computed<string>(() => {
  return RenderAuthUserInstance?.value?.name ?? 'User avatar'
})

const avatarProps = computed<Pick<AvatarProps, 'src' | 'alt'>>(() => ({
  src: avatarSrc.value,
  alt: userAlt.value,
  as: avatarAsConfig.value,
}))
</script>
